import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized
      localStorage.removeItem('token');
    }
    return Promise.reject(error);
  }
);

// Sensor API
export const sensorAPI = {
  getLatest: (deviceId?: string) => {
    const params = deviceId ? { deviceId } : {};
    return api.get('/sensors/latest', { params });
  },
  
  getHistory: (deviceId: string, from?: string, to?: string) => {
    return api.get('/sensors/history', {
      params: { deviceId, from, to },
    });
  },
  
  getStatistics: (deviceId: string, from?: string, to?: string, window?: string) => {
    return api.get('/sensors/statistics', {
      params: { deviceId, from, to, window },
    });
  },
  
  getStatus: () => {
    return api.get('/sensors/status');
  },
  
  exportData: (deviceId: string, from?: string, to?: string, format: 'csv' | 'json' = 'json') => {
    return api.get('/sensors/export', {
      params: { deviceId, from, to, format },
      responseType: format === 'csv' ? 'blob' : 'json',
    });
  },
  
  refresh: (deviceId: string) => {
    return api.post('/sensors/refresh', { deviceId });
  },
};

// Alert API
export const alertAPI = {
  getAlerts: (params?: {
    deviceId?: string;
    severity?: string;
    acknowledged?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    return api.get('/alerts', { params });
  },
  
  getAlert: (id: string) => {
    return api.get(`/alerts/${id}`);
  },
  
  acknowledgeAlert: (id: string, userId?: string) => {
    return api.put(`/alerts/${id}/acknowledge`, { userId });
  },
  
  clearAcknowledged: () => {
    return api.delete('/alerts/acknowledged');
  },
  
  getThresholds: (deviceId?: string) => {
    const url = deviceId ? `/alerts/thresholds/${deviceId}` : '/alerts/thresholds';
    return api.get(url);
  },
  
  updateThresholds: (deviceId: string | null, thresholds: {
    moistureMin?: number;
    moistureMax?: number;
    cooldownMinutes?: number;
  }) => {
    const url = deviceId ? `/alerts/thresholds/${deviceId}` : '/alerts/thresholds';
    return api.put(url, thresholds);
  },
  
  getStatistics: () => {
    return api.get('/alerts/stats/summary');
  },
};

// Analytics API
export const analyticsAPI = {
  getTrends: (deviceId: string, period?: string, interval?: string) => {
    return api.get(`/analytics/trends/${deviceId}`, {
      params: { period, interval },
    });
  },
  
  getDailySummary: (deviceId: string, date?: string) => {
    return api.get(`/analytics/daily-summary/${deviceId}`, {
      params: { date },
    });
  },
  
  getWeeklyComparison: (deviceId: string) => {
    return api.get(`/analytics/weekly-comparison/${deviceId}`);
  },
  
  getIrrigationRecommendation: (deviceId: string) => {
    return api.get(`/analytics/irrigation-recommendation/${deviceId}`);
  },
  
  getPerformance: (deviceId: string, period?: string) => {
    return api.get(`/analytics/performance/${deviceId}`, {
      params: { period },
    });
  },
};

// Health check
export const checkHealth = () => {
  return axios.get(`${API_URL.replace('/api', '')}/health`);
};

export default api;
